import { useId } from 'react';
import { FACEBOOK_LOGO, INSTAGRAM_GLYPH_GRADIENT, TIKTOK_ICON } from '@crewcast/shared';

// Official brand marks with the same size/className props as lucide icons,
// so they can be swapped in wherever a lucide icon is rendered.
interface BrandIconProps {
  size?: number | string;
  className?: string;
}

// Full-colour glyph: Meta's gradient image clipped to the glyph outline.
export function InstagramIcon({ size = 24, className }: BrandIconProps) {
  // Clip ids are document-global, so each instance needs its own. useId's colons
  // aren't safe inside url(#...).
  const clipId = `ig-clip-${useId().replace(/:/g, '')}`;
  const { viewBox, clipPath, image } = INSTAGRAM_GLYPH_GRADIENT;
  return (
    <svg width={size} height={size} viewBox={viewBox} className={className} aria-hidden="true">
      <defs>
        <clipPath id={clipId}>
          <path d={clipPath} />
        </clipPath>
      </defs>
      <image href={image} width="100%" height="100%" clipPath={`url(#${clipId})`} />
    </svg>
  );
}

// Marks drawn as stacked solid-colour layers, each keeping its official fill.
function LayeredIcon({ icon, size = 24, className }: BrandIconProps & { icon: typeof TIKTOK_ICON }) {
  return (
    <svg width={size} height={size} viewBox={icon.viewBox} className={className} aria-hidden="true">
      {icon.layers.map((layer, i) => (
        <path key={i} d={layer.d} fill={layer.fill} />
      ))}
    </svg>
  );
}

// Full-colour badge; TikTok's kit has no single-colour version.
export function TikTokIcon(props: BrandIconProps) {
  return <LayeredIcon icon={TIKTOK_ICON} {...props} />;
}

export function FacebookIcon(props: BrandIconProps) {
  return <LayeredIcon icon={FACEBOOK_LOGO} {...props} />;
}
